import { connect } from 'react-redux';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Checkbox, TextField } from '@mui/material';
import { useParams } from 'react-router-dom';

import { ReactComponent as RadioOffIcon } from '../assets/images/radio_off2.svg';
import { ReactComponent as RadioOnIcon } from '../assets/images/radio_on2.svg';
import { ReactComponent as CloseIcon } from '../assets/images/close.svg';
import { checkMenuAvailability } from '../constants/commonFunc';
import { useSelector } from "react-redux";
import AllergensMenu from './AllergensMenu';
import { formatPrice, HIDE_ITEM_NOTES_SLUGS, MENU_TYPE } from '../constants';
import NutritionBadge from './menuCard/NutritionBadge';

const ImageContainer = styled(Box)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 220px;
  background-image: url(${(props) => props.image});
  background-size: cover;
  background-position: center;
  z-index: 1;
`;

const ContentContainer = styled(Box)`
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  padding-left: 16px;
  padding-right: 16px;
  height: 100%;
  overflow-y: auto;
  z-index: 2;
  box-sizing: border-box;
`;

const Item = styled(Box)`
  margin-top: ${(props) => (props.noMenuImage ? '20px' : '16px')};
  margin-bottom: 16px;
  background-color: ${(props) => (props?.backgroundColor ?? "#ffffff")};
  border-radius: 8px;
  display: flex;
  // position: sticky;
  // top: 16px;
  z-index: 4;
  box-shadow: 0 -4px 4px rgba(0, 0, 0, 0.1);
`;

const FoodBadgeInfoContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  column-gap: 6px;
  row-gap: 16px;
  flex-wrap: wrap;
  border-top: 1px solid #f0f0f0;
  padding-top: 8px;
  margin-top: 8px;
`;

const AllergenContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-ttems: center;
  gap: 8px;
  flex-wrap: wrap;
  border-top: 1px solid #f0f0f0;
  padding-top: 8px;
  margin-top: 8px;
`;

const AddItemToCartInterface = ({
  itemData,
  menuList,
  onModifierClick,
  addedModifiers,
  onClose,
  onNotesChange,
  notes,
  allergenList,
  menuContainerRef,
  menuAttributeList,
}) => {
  const urlParams = useParams();
  const isOrderingMode = !!urlParams?.table_id;
  const showNotes = !HIDE_ITEM_NOTES_SLUGS.includes(
    urlParams?.slug?.toLowerCase()
  );

  const menuTypeIcon = MENU_TYPE.find(
    (t) => t.title?.toLowerCase() === itemData?.extras?.menuType?.toLowerCase()
  )?.icon ?? null;

  const { restData } = useSelector((state) => state.apiData) || {};

  const {
    menuPopupItemBgColor = null,
    menuPopupItemFontColor = null,
    menuPopupItemPriceFontSize = null,
    menuPopupModifierBgColor = null,
    menuPopupModifierFontColor = null,
    menuPopupModifierPriceFontSize = null
  } = restData?.themeColors || {};

  const getAllergen = (allergenId) => {
    return (allergenList ?? []).find((a) => a._id === allergenId) ?? null;
  };

  const getAttribute = (attributeId) => {
    return (menuAttributeList ?? []).find(x => x._id === attributeId) ?? null;
  }

  const resolvedAllergens = (itemData?._idAllergens ?? [])
    .map((id) => getAllergen(id))
    .filter(Boolean);

  return (
    <Box sx={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <ImageContainer image={itemData?.images?.[0]} />

      <Box
        sx={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          cursor: 'pointer',
          width: '35px',
          height: '35px',
          position: 'absolute',
          top: '16px',
          right: '16px',
          zIndex: 3,
        }}
        onClick={onClose}
      >
        <CloseIcon
          height={20}
          width={20}
        />
      </Box>

      <ContentContainer
        sx={{
          pb: '100px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            mt: itemData?.images.length ? '200px' : '60px',
            backgroundColor: menuPopupItemBgColor ?? '#FFFFFF',
            borderRadius: '8px',
            padding: '16px',
            zIndex: 3,
            boxShadow: '0 -4px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter !important',
                fontWeight: 600,
                fontSize: 18,
                color: `${menuPopupItemFontColor ?? "#011C3A"} !important`,
                textTransform: 'capitalize',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                gap: 1,
              }}
            >
              {itemData?.name}

              {menuTypeIcon}
            </Typography>

            <Typography
              sx={{
                color: `${menuPopupItemFontColor ?? "#000000"} !important`,
                fontFamily: 'Inter !important',
                fontWeight: '600 !important',
                fontSize: `${menuPopupItemPriceFontSize ?? 18}px !important`,
              }}
            >
              {formatPrice(itemData?.price?.value, itemData?.price?.currency)}
            </Typography>
          </Box>

          <Box>
            {(itemData?.description &&
              <Typography
                sx={{
                  mt: 1.5,
                  fontFamily: 'Inter !important',
                  fontWeight: '400 !important',
                  fontSize: '16px !important',
                  color: '#011C3A',
                }}
              >
                {itemData?.description}
              </Typography>)}

            {itemData?.attributes?.length > 0 && (
              <FoodBadgeInfoContainer style={{ paddingTop: "16px" }}>
                {itemData.attributes.map((item, index) => {
                  const attribute = getAttribute(item._idAttribute);
                  if (!attribute) return null;
                  return <NutritionBadge attribute={attribute} value={item.value} />
                })}
              </FoodBadgeInfoContainer>
            )}

          </Box>
        </Box>
        <AllergensMenu item={itemData} resolvedAllergens={resolvedAllergens} menuContainerRef={menuContainerRef} />
        {(itemData?.modifiers ?? []).map((item, i) => (
          <Item
            key={i}
            sx={{
              flexDirection: 'column',
            }}
            backgroundColor={`${menuPopupModifierBgColor ?? '#FFFFFF'} !important`}
            noMenuImage={itemData?.images.length === 0}
          >
            <Box
              sx={{
                display: 'flex',
                borderBottom: '1px solid #cccccc',
                alignItems: 'center',
                padding: '16px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Typography
                  sx={{
                    color: `${menuPopupModifierFontColor ?? "#000"} !important`,
                    fontFamily: 'Inter !important',
                    fontSize: '16px !important',
                    fontWeight: '600 !important',
                  }}
                >
                  {item.header}
                </Typography>
                <Typography
                  sx={{
                    color: `${menuPopupModifierFontColor ?? "#000"} !important`,
                    fontFamily: 'Inter !important',
                    fontSize: '14px !important',
                    fontWeight: '500 !important',
                  }}
                >
                  {!isOrderingMode ? 'Available options' : item?.multiSelection
                    ? 'Select any options'
                    : 'Select any 1 option'}
                </Typography>
              </Box>
              {item.required ? (
                <Box
                  ml={1}
                  px={1}
                  py={0.5}
                  sx={{
                    borderRadius: '5px',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontFamily: 'GilroyMedium !important',
                      fontSize: '14px !important',
                      textTransform: 'uppercase !important',
                    }}
                  >
                    required
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Box
              sx={{
                p: 2,
              }}
            >
              {item.items.map((x, i2) => {
                let menu_data = null;
                let menu_name = '';
                let menu_price = '';
                let find_index = menuList.findIndex((y) => y?._id === x
                  && checkMenuAvailability(y).isItemAvailable);
                if (find_index >= 0) {
                  menu_data = menuList?.[find_index];
                  menu_name = menu_data?.name;
                  menu_price = menu_data?.price?.value;
                }
                if (!menu_data) {
                  return null;
                }
                return (
                  <Box
                    key={'i' + i2}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      pt: 1.5,
                    }}
                    onClick={() => onModifierClick(item, menu_data)}
                  >
                    <Box
                      sx={{
                        flex: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Inter !important',
                          fontSize: '14px !important',
                          textTransform: 'capitalize',
                          fontWeight: '600 !important',
                          color: `${menuPopupModifierFontColor ?? "#011C3A"} !important`,
                        }}
                      >
                        {menu_name}
                      </Typography>
                    </Box>

                    {menu_price ? (
                      <Typography
                        sx={{
                          mr: 1,
                          color: `${menuPopupModifierFontColor ?? "#000"} !important`,
                          fontFamily: 'Inter !important',
                          fontSize: `${menuPopupModifierPriceFontSize ?? 14}px !important`,
                          fontWeight: '500 !important',
                        }}
                      >
                        {formatPrice(menu_price, menu_data?.price?.currency)}
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          fontFamily: 'Inter !important',
                          fontSize: '14px !important',
                          fontWeight: '500 !important',
                          mr: 1,
                        }}
                      >
                        FREE
                      </Typography>
                    )}

                    {isOrderingMode && (
                      <Box>
                        {item.multiSelection ? (
                          <Checkbox
                            checked={addedModifiers?.some(
                              (y) =>
                                y._idMenuItem === x && y._idModifier === item._id
                            )}
                            sx={{
                              color: '#000',
                              padding: 0,
                              '& .MuiSvgIcon-root': { fontSize: 20 },
                              '&.Mui-checked': {
                                color: '#000',
                              },
                            }}
                          />
                        ) : addedModifiers?.some(
                          (y) =>
                            y._idMenuItem === x && y._idModifier === item._id
                        ) ? (
                          <RadioOnIcon width={20} height={20} />
                        ) : (
                          <RadioOffIcon width={20} height={20} />
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Item>
        ))}

        {isOrderingMode && showNotes && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              mt: 2,
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              padding: '16px',
              zIndex: 3,
              boxShadow: '0 -4px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter !important',
                fontSize: '16px !important',
                fontWeight: '600 !important',
              }}
            >
              Add Notes
            </Typography>
            <TextField
              id='item-dishnote'
              multiline
              variant='standard'
              onChange={(e) => onNotesChange(e.target.value)}
              value={notes || ''}
            />
          </Box>
        )}
      </ContentContainer>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  allergenList: state.apiData.allergenList,
  menuAttributeList: state.apiData.menuAttributesList,
});

export default connect(mapStateToProps)(AddItemToCartInterface);
